import React, { useState } from "react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import { Eye, EyeOff } from "lucide-react";
import { friendlyAuthError } from "../../utils/friendlyAuthError";
import { getRqAttribution } from "../../utils/rqAttribution";

function isAccountAlreadySetUpError(error) {
  const message = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();

  return (
    message.includes("account already set up") ||
    message.includes("already exists") ||
    message.includes("duplicate key") ||
    code === "23505"
  );
}

export default function TradieAuth() {
  const { showToast } = useToast();
  // "signin" | "signup" | "forgot"
  const [mode, setMode] = useState("signin");
  const [loading, setLoading] = useState(false);

  const [fullName, setFullName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Transient state for the forgot-password flow. We flip `resetSent` on
  // success so the form replaces itself with a confirmation message without
  // needing a separate route or modal.
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  async function ensureTradieSetup(userOverride = null) {
    const user =
      userOverride ||
      (
        await supabase.auth.getUser()
      ).data.user;

    if (!user) {
      throw new Error("No authenticated user found.");
    }

    const metadata = user.user_metadata || {};

    const setupFullName = metadata.full_name || fullName || "";
    const setupBusinessName =
      metadata.business_name || businessName || "My Business";
    const setupPhone = metadata.phone || phone || "";
    const setupEmail = user.email || email;

    const { error: setupError } = await supabase.rpc("create_tradie_account", {
      p_full_name: setupFullName,
      p_business_name: setupBusinessName,
      p_email: setupEmail,
      p_initial_credits: 300,
    });

    if (setupError && !isAccountAlreadySetUpError(setupError)) {
      throw setupError;
    }

    if (setupPhone.trim()) {
      const { error: phoneError } = await supabase
        .from("profiles")
        .update({ phone: setupPhone.trim() })
        .eq("id", user.id);
      if (phoneError) throw phoneError;
    }

    // Stamp campaign attribution onto the profile, fire-and-forget.
    // Never block or fail signup on this.
    const attr = getRqAttribution();
    if (attr) {
      try {
        await supabase
          .from("profiles")
          .update({ attribution: attr })
          .eq("id", user.id);
      } catch (_) {
        // Attribution is best-effort only.
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "signup") {
        if (!fullName.trim()) {
          showToast("Please add your name.", "error");
          setLoading(false);
          return;
        }
        if (!businessName.trim()) {
          showToast("Please add your business name.", "error");
          setLoading(false);
          return;
        }
        if (!phone.trim()) {
          showToast("Please add your contact number.", "error");
          setLoading(false);
          return;
        }

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName || "",
              business_name: businessName || "My Business",
              phone: phone || "",
            },
          },
        });

        if (signUpError) throw signUpError;

        const user = signUpData?.user;
        if (!user) {
          throw new Error(
            "Signup succeeded, but no user was returned. Check your Supabase Auth settings."
          );
        }

        if (signUpData.session) {
          try {
            await ensureTradieSetup(user);
          } catch (setupError) {
            // Profile/business creation failed. Sign out so the user isn't
            // left stuck on "Finishing account setup" with no escape.
            await supabase.auth.signOut();
            throw setupError;
          }
          // Refresh the JWT so onAuthStateChange re-reads the new profile.
          await supabase.auth.refreshSession();
          showToast("Account created successfully. You are now signed in.", "success");
        } else {
          showToast(
            "Account created. Please confirm your email, then sign in.",
            "info"
          );
        }
      } else {
        // Sign-in path. We fetch the profile BEFORE calling the tradie setup
        // RPC so we can skip it for customers — otherwise create_tradie_account
        // could overwrite a legitimate customer's role/business. App.js reads
        // the profile role via useAuth and routes to the correct dashboard,
        // so a customer who signs in here will still land on the customer
        // dashboard, not the tradie one.
        const { data: signInData, error: signInError } =
          await supabase.auth.signInWithPassword({ email, password });

        if (signInError) throw signInError;

        const signedInUserId = signInData?.user?.id;
        let existingRole = null;
        if (signedInUserId) {
          const { data: profileRow } = await supabase
            .from("profiles")
            .select("role")
            .eq("id", signedInUserId)
            .maybeSingle();
          existingRole = profileRow?.role || null;
        }

        // Only run the tradie-setup RPC for accounts that don't already have
        // a customer profile. Tradie / business / missing-profile accounts
        // still need setup to create (or verify) their business + wallet.
        if (existingRole !== "customer") {
          await ensureTradieSetup(signInData?.user || null);
        }

        // Refresh so onAuthStateChange re-reads the profile (role-based
        // routing in App.js picks up from here).
        await supabase.auth.refreshSession();
      }
    } catch (error) {
      showToast(friendlyAuthError(error.message) || "Authentication failed.", "error");
    } finally {
      setLoading(false);
    }
  }

  // Forgot-password submit. Sends a Supabase recovery email; on return the
  // user lands back on this origin with #type=recovery in the hash, which
  // App.js detects and renders the ResetPasswordPage.
  async function handleForgotSubmit(e) {
    e.preventDefault();
    const target = (resetEmail || "").trim();
    if (!target) {
      showToast("Please enter your email address.", "error");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(target, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      showToast(friendlyAuthError(err.message) || "Could not send reset email.", "error");
    } finally {
      setLoading(false);
    }
  }

  function switchMode(nextMode) {
    setMode(nextMode);
    // Reset the forgot-password UI whenever the user navigates away so it
    // doesn't show the stale "check your email" confirmation next time.
    setResetSent(false);
    setResetEmail("");
    setShowPassword(false);
  }

  return (
    <div className="two-grid">
      <Card className="hero dark">
        <Badge>Tradie access</Badge>
        <h1>Sign in to manage your leads, credits, and quotes.</h1>
        <p>
          Each tradie account is linked to its own business, wallet, unlocks,
          and quote history.
        </p>
      </Card>

      <Card>
        <h2>
          {mode === "signup"
            ? "Create tradie account"
            : mode === "forgot"
            ? "Reset your password"
            : "Sign in"}
        </h2>

        {mode === "forgot" ? (
          resetSent ? (
            <>
              <p className="muted">
                Check your email for a password reset link. Click it and you'll
                come back here to set a new password.
              </p>
              <div className="button-row">
                <Button secondary onClick={() => switchMode("signin")}>
                  Back to sign in
                </Button>
              </div>
            </>
          ) : (
            <form onSubmit={handleForgotSubmit} className="form-grid">
              <p className="muted full" style={{ margin: 0 }}>
                Enter the email on your tradie account and we'll send you a
                link to set a new password.
              </p>
              <div className="full">
                <label>Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="full button-row">
                <Button type="submit" disabled={loading}>
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
                <Button
                  type="button"
                  secondary
                  onClick={() => switchMode("signin")}
                  disabled={loading}
                >
                  Back to sign in
                </Button>
              </div>
            </form>
          )
        ) : (
          <form onSubmit={handleSubmit} className="form-grid">
            {mode === "signup" && (
              <>
                <div>
                  <label>Full name</label>
                  <input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </div>

                <div>
                  <label>Business name</label>
                  <input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                  />
                </div>

                <div className="full">
                  <label>Contact number</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="04xx xxx xxx"
                    autoComplete="tel"
                  />
                </div>
              </>
            )}

            <div className="full">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="full">
              <label>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* Forgot password link — sign-in only so the signup form stays
                  minimal. Uses the existing .link-btn style. */}
              {mode === "signin" && (
                <p className="muted small" style={{ marginTop: "6px" }}>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => switchMode("forgot")}
                  >
                    Forgot password?
                  </button>
                </p>
              )}
            </div>

            <div className="full button-row">
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Please wait..."
                  : mode === "signup"
                  ? "Create account"
                  : "Sign in"}
              </Button>

              <Button
                type="button"
                secondary
                onClick={() =>
                  switchMode(mode === "signup" ? "signin" : "signup")
                }
              >
                {mode === "signup"
                  ? "Already have an account?"
                  : "Create new account"}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
